import type { ThreadLockHolder, ThreadLockStateData } from "@productlane/lib"
import { THREAD_LOCK_HEARTBEAT_MS } from "@productlane/lib"

import { acquireWorkspaceSocket } from "@/lib/sockets/workspace-socket"

interface Identity {
	workspaceId: string
	userId: string
	name: string
	imageUrl?: string | null
}

interface ClaimedData {
	threadId: string
	holder: ThreadLockHolder | null
	isMine: boolean
}

interface ThreadLockSocketOptions {
	identity: Identity
	onState: (data: ThreadLockStateData) => void
	onClaimed?: (data: ClaimedData) => void
}

export interface ThreadLockSocket {
	claim: (threadId: string) => void
	release: (threadId: string) => void
	close: () => void
}

/**
 * Holds the thread an agent has open, so the same email cannot be answered
 * twice. The claim is refreshed on a timer and dropped when the socket closes.
 */
export function createThreadLockSocket({
	identity,
	onState,
	onClaimed,
}: ThreadLockSocketOptions): ThreadLockSocket {
	const { socket, release } = acquireWorkspaceSocket()

	let heldThreadId: string | null = null
	let heartbeat: ReturnType<typeof setInterval> | null = null

	const subscribe = () => {
		socket.emit("thread-lock:subscribe", {
			workspaceId: identity.workspaceId,
			userId: identity.userId,
		})
	}

	const payload = (threadId: string) => ({
		workspaceId: identity.workspaceId,
		threadId,
		userId: identity.userId,
		name: identity.name,
		imageUrl: identity.imageUrl,
	})

	const stopHeartbeat = () => {
		if (!heartbeat) return
		clearInterval(heartbeat)
		heartbeat = null
	}

	const startHeartbeat = (threadId: string) => {
		stopHeartbeat()
		heartbeat = setInterval(() => {
			if (document.visibilityState !== "visible") return
			socket.emit("thread-lock:heartbeat", payload(threadId))
		}, THREAD_LOCK_HEARTBEAT_MS)
	}

	const handleConnect = () => {
		subscribe()
		if (heldThreadId) socket.emit("thread-lock:claim", payload(heldThreadId))
	}

	const handleState = (data: ThreadLockStateData) => {
		onState(data)
	}

	const handleClaimed = (data: ClaimedData) => {
		onClaimed?.(data)
	}

	socket.on("connect", handleConnect)
	socket.on("thread-lock:state", handleState)
	socket.on("thread-lock:claimed", handleClaimed)

	if (socket.connected) handleConnect()

	return {
		claim: (threadId) => {
			if (heldThreadId === threadId) return
			if (heldThreadId)
				socket.emit("thread-lock:release", payload(heldThreadId))
			heldThreadId = threadId
			socket.emit("thread-lock:claim", payload(threadId))
			startHeartbeat(threadId)
		},
		release: (threadId) => {
			if (heldThreadId !== threadId) return
			heldThreadId = null
			stopHeartbeat()
			socket.emit("thread-lock:release", payload(threadId))
		},
		close: () => {
			stopHeartbeat()
			if (heldThreadId)
				socket.emit("thread-lock:release", payload(heldThreadId))
			heldThreadId = null
			socket.off("connect", handleConnect)
			socket.off("thread-lock:state", handleState)
			socket.off("thread-lock:claimed", handleClaimed)
			release()
		},
	}
}
